import { useState, useRef, useCallback } from 'react';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import jsPDF from 'jspdf';
// import 'jspdf-autotable';
import { autoTable } from 'jspdf-autotable'

// import * as XLSX from 'xlsx';
import XLSX from 'xlsx-js-style';

import './styles/Happea.css';

const REQUIRED_HEADERS = [
    'Customer name', 'Order number', 'Customer phone', 'Service address',
    'Service city', 'Lineitem name', 'Lineitem option', 'Lineitem quantity',
    'Total', 'Payment Status', 'Service name', 'Internal note'
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseCSV(text) {
    const results = [];
    const lines = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"') {
            if (inQuotes && text[i + 1] === '"') {
                current += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (ch === ',' && !inQuotes) {
            lines.push(current);
            current = '';
        } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
            if (current || lines.length) {
                lines.push(current);
                if (lines.some(c => c.trim())) {
                    results.push(lines.slice());
                }
                lines.length = 0;
            }
            current = '';
            if (ch === '\r' && text[i + 1] === '\n') i++;
        } else {
            current += ch;
        }
    }
    if (current || lines.length) {
        lines.push(current);
        if (lines.some(c => c.trim())) {
            results.push(lines.slice());
        }
    }
    return results;
}

function extractDateFromLineitemName(name) {
    if (!name) return null;
    // Match pattern like "23/02 MONDAY" at start
    const m = name.match(/^(\d{1,2})\/(\d{2})\s+\w+/i);
    if (m) {
        const day = parseInt(m[1], 10);
        const month = parseInt(m[2], 10);
        const year = new Date().getFullYear();
        return { day, month, year, key: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` };
    }
    return null;
}

function extractDateFromLineitemOption(option) {
    if (!option) return null;
    // 1️⃣ First check if the string contains "delivery date" in any case
    if (!/delivery\s*date/i.test(option)) {
        return null;
    }

    // 2️⃣ Extract the date ONLY if it appears after "delivery date"
    const m = option.match(
        /delivery\s*date[\s:?;]*?(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/i
    );

    if (!m) return null;

    const day = parseInt(m[1], 10);
    const monthIdx = MONTHS.findIndex(
        mn => mn.toLowerCase() === m[2].toLowerCase()
    );

    if (monthIdx === -1) return null;

    const month = monthIdx + 1;
    const year = parseInt(m[3], 10);

    return {
        day,
        month,
        year,
        key: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    };
}

function formatDateLabel(key) {
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y} — ${days[date.getDay()]}`;
}

function processCSV(text) {
    const rows = parseCSV(text);
    if (rows.length < 2) return {};

    const headerRow = rows[0].map(h => h.trim());
    const colMap = {};
    REQUIRED_HEADERS.forEach(h => {
        const idx = headerRow.findIndex(c => c.toLowerCase() === h.toLowerCase());
        if (idx !== -1) colMap[h] = idx;
    });

    const grouped = {};

    for (let i = 1; i < rows.length; i++) {
        const cells = rows[i];
        const record = {};
        REQUIRED_HEADERS.forEach(h => {
            record[h] = colMap[h] !== undefined ? (cells[colMap[h]] || '').trim() : '';
        });

        let dateInfo = extractDateFromLineitemName(record['Lineitem name']);
        if (!dateInfo) {
            dateInfo = extractDateFromLineitemOption(record['Lineitem option']);
        }

        const dateKey = dateInfo ? dateInfo.key : 'unknown';
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(record);
    }

    return grouped;
}

function getKitchenSummary(records) {
    const meals = {};
    records.forEach(r => {
        let name = (r['Lineitem name'] || '').replace(/^\d{1,2}\/\d{2}\s+\w+[-–—\s]*/, '').trim();
        if (!name) name = '(unnamed item)';
        const qty = parseInt(r['Lineitem quantity'], 10) || 1;

        // Determine size: default to normal if no keyword match
        let sizeType = 'normal';
        if (/\bUPSIZE\b/i.test(name)) sizeType = 'upsize';

        // Extract base meal name
        const baseName = name
            .replace(/[-–—]\s*(NORMAL|UPSIZE)\s*[-–—]?\s*/gi, '')
            .replace(/\s*\d+g\s*$/, '')
            .replace(/\s+/g, ' ')
            .trim();

        if (!meals[baseName]) {
            meals[baseName] = { normal: 0, normalCustom: 0, upsize: 0, upsizeCustom: 0, normalCustomTexts: [], upsizeCustomTexts: [] };
        }

        // Check for CUSTOMISE in lineitem option (supports multiple CUSTOMISE:: lines)
        const option = r['Lineitem option'] || '';
        const customMatches = [...option.matchAll(/CUSTOMISE\s*:{2}\s*(.*)/gi)];
        const customTexts = customMatches.map(m => m[1].trim()).filter(Boolean);
        const hasCustomise = customTexts.length > 0;
        const customText = customTexts.join(',');

        const customerName = r['Customer name'] || '';

        if (sizeType === 'normal') {
            if (hasCustomise) {
                meals[baseName].normalCustom += qty;
                meals[baseName].normalCustomTexts.push({ text: customText, qty, customer: customerName });
            } else {
                meals[baseName].normal += qty;
            }
        } else {
            if (hasCustomise) {
                meals[baseName].upsizeCustom += qty;
                meals[baseName].upsizeCustomTexts.push({ text: customText, qty, customer: customerName });
            } else {
                meals[baseName].upsize += qty;
            }
        }
    });

    return Object.entries(meals).sort((a, b) => {
        const totalA = a[1].normal + a[1].normalCustom + a[1].upsize + a[1].upsizeCustom;
        const totalB = b[1].normal + b[1].normalCustom + b[1].upsize + b[1].upsizeCustom;
        return totalB - totalA;
    });
}

export default function Happea() {
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [groupedData, setGroupedData] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [activeTab, setActiveTab] = useState('kitchen');
    const [showColumnPicker, setShowColumnPicker] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [exportFormat, setExportFormat] = useState('xlsx');
    const inputRef = useRef(null);

    const handleDrag = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);
    const handleDragIn = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }, []);
    const handleDragOut = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault(); e.stopPropagation(); setDragging(false);
        const f = e.dataTransfer.files?.[0];
        if (f && f.name.endsWith('.csv')) { setFile(f); setGroupedData(null); setSelectedDate(null); }
    }, []);

    const handleFileSelect = (e) => {
        const f = e.target.files?.[0];
        if (f && f.name.endsWith('.csv')) { setFile(f); setGroupedData(null); setSelectedDate(null); }
    };

    const removeFile = () => { setFile(null); setGroupedData(null); setSelectedDate(null); if (inputRef.current) inputRef.current.value = ''; };

    const processFile = () => {
        if (!file) return;
        setProcessing(true);
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const grouped = processCSV(text);
            setGroupedData(grouped);
            const keys = Object.keys(grouped).sort((a, b) => (a === 'unknown' ? 1 : b === 'unknown' ? -1 : a.localeCompare(b)));
            // Default to today's date if available, otherwise first date
            const today = new Date();
            const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            if (grouped[todayKey]) {
                setSelectedDate(todayKey);
            } else if (keys.length > 0) {
                setSelectedDate(keys[0]);
            }
            setProcessing(false);
        };
        reader.readAsText(file);
    };

    const sortedDateKeys = groupedData
        ? Object.keys(groupedData).sort((a, b) => (a === 'unknown' ? 1 : b === 'unknown' ? -1 : a.localeCompare(b)))
        : [];

    const currentRecords = groupedData && selectedDate ? (groupedData[selectedDate] || []) : [];
    const kitchenSummary = currentRecords.length > 0 ? getKitchenSummary(currentRecords) : [];

    const exportKitchenPDF = (columnsToExport) => {
        if (kitchenSummary.length === 0) return;
        const filtered = columnsToExport
            ? kitchenSummary.filter(([name]) => columnsToExport.includes(name))
            : kitchenSummary;
        if (filtered.length === 0) return;

        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const dateLabel = selectedDate === 'unknown' ? 'No Date' : formatDateLabel(selectedDate);
        doc.setFontSize(14);
        doc.text(`Kitchen Sheet — ${dateLabel}`, 14, 15);

        const headers = ['', ...filtered.map(([name]) => name), 'Total'];
        const rows = [
            ['Normal', ...filtered.map(([, d]) => d.normal || '—'), filtered.reduce((s, [, d]) => s + d.normal, 0)],
            ['Normal Custom', ...filtered.map(([, d]) =>
                d.normalCustomTexts.length > 0
                    ? d.normalCustomTexts.map(c => `${c.qty}x ${c.text}${c.customer ? ' (' + c.customer + ')' : ''}`).join('\n')
                    : '—'
            ), filtered.reduce((s, [, d]) => s + d.normalCustom, 0)],
            ['Upsize', ...filtered.map(([, d]) => d.upsize || '—'), filtered.reduce((s, [, d]) => s + d.upsize, 0)],
            ['Upsize Custom', ...filtered.map(([, d]) =>
                d.upsizeCustomTexts.length > 0
                    ? d.upsizeCustomTexts.map(c => `${c.qty}x ${c.text}${c.customer ? ' (' + c.customer + ')' : ''}`).join('\n')
                    : '—'
            ), filtered.reduce((s, [, d]) => s + d.upsizeCustom, 0)],
            ['Total', ...filtered.map(([, d]) => d.normal + d.normalCustom + d.upsize + d.upsizeCustom), filtered.reduce((s, [, d]) => s + d.normal + d.normalCustom + d.upsize + d.upsizeCustom, 0)],
        ];
        autoTable(doc, {
            head: [headers],
            body: rows,
            startY: 22,
            theme: "grid",
            styles: { fontSize: 7, cellPadding: 2, overflow: "linebreak" },
            headStyles: { fillColor: [137, 180, 135], textColor: 255, fontStyle: "bold" },
            columnStyles: { 0: { fontStyle: "bold", cellWidth: 28 } },
            didParseCell: (data) => {
                if (data.row.index === rows.length - 1) {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.styles.fillColor = [238, 245, 238];
                }
            },
        });

        doc.save(`kitchen-${selectedDate}.pdf`);
    };

    const buildKitchenSpreadsheetData = (columnsToExport) => {
        if (kitchenSummary.length === 0) return null;
        const filtered = columnsToExport
            ? kitchenSummary.filter(([name]) => columnsToExport.includes(name))
            : kitchenSummary;
        if (filtered.length === 0) return null;

        const headers = ['', ...filtered.map(([name]) => name), 'Total'];
        const rows = [];

        // Normal row
        rows.push(['Normal', ...filtered.map(([, d]) => d.normal || ''), filtered.reduce((s, [, d]) => s + d.normal, 0)]);

        // Normal Custom — grid layout: columns share rows
        const normalCustomCols = filtered.map(([, d]) => d.normalCustomTexts.map(c => `${c.qty}x ${c.text}${c.customer ? ' (' + c.customer + ')' : ''}`));
        const maxNormalRows = Math.max(...normalCustomCols.map(a => a.length), 0);
        if (maxNormalRows > 0) {
            for (let i = 0; i < maxNormalRows; i++) {
                const row = Array(headers.length).fill('');
                if (i === 0) row[0] = 'Normal Custom';
                normalCustomCols.forEach((col, ci) => { if (col[i]) row[ci + 1] = col[i]; });
                if (i === maxNormalRows - 1) row[headers.length - 1] = filtered.reduce((s, [, d]) => s + d.normalCustom, 0);
                rows.push(row);
            }
        } else {
            rows.push(['Normal Custom', ...filtered.map(() => ''), filtered.reduce((s, [, d]) => s + d.normalCustom, 0)]);
        }

        // Upsize row
        rows.push(['Upsize', ...filtered.map(([, d]) => d.upsize || ''), filtered.reduce((s, [, d]) => s + d.upsize, 0)]);

        // Upsize Custom — grid layout: columns share rows
        const upsizeCustomCols = filtered.map(([, d]) => d.upsizeCustomTexts.map(c => `${c.qty}x ${c.text}${c.customer ? ' (' + c.customer + ' - UPSIZE)' : ''}`));
        const maxUpsizeRows = Math.max(...upsizeCustomCols.map(a => a.length), 0);
        if (maxUpsizeRows > 0) {
            for (let i = 0; i < maxUpsizeRows; i++) {
                const row = Array(headers.length).fill('');
                if (i === 0) row[0] = 'Upsize Custom';
                upsizeCustomCols.forEach((col, ci) => { if (col[i]) row[ci + 1] = col[i]; });
                if (i === maxUpsizeRows - 1) row[headers.length - 1] = filtered.reduce((s, [, d]) => s + d.upsizeCustom, 0);
                rows.push(row);
            }
        } else {
            rows.push(['Upsize Custom', ...filtered.map(() => ''), filtered.reduce((s, [, d]) => s + d.upsizeCustom, 0)]);
        }

        // Total row
        rows.push(['Total', ...filtered.map(([, d]) => d.normal + d.normalCustom + d.upsize + d.upsizeCustom), filtered.reduce((s, [, d]) => s + d.normal + d.normalCustom + d.upsize + d.upsizeCustom, 0)]);

        return { headers, rows };
    };

    const exportKitchenXLSX = (columnsToExport) => {
        const data = buildKitchenSpreadsheetData(columnsToExport);
        if (!data) return;
        const { headers, rows } = data;
        const aoa = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // Style header row
        headers.forEach((_, ci) => {
            const ref = XLSX.utils.encode_cell({ r: 0, c: ci });
            if (ws[ref]) {
                ws[ref].s = {
                    font: { bold: true, color: { rgb: 'FFFFFF' } },
                    fill: { fgColor: { rgb: '89B487' } },
                    alignment: { horizontal: 'center' },
                };
            }
        });

        // Style total row (last row) and first column bold
        const totalRowIdx = aoa.length - 1;
        headers.forEach((_, ci) => {
            const ref = XLSX.utils.encode_cell({ r: totalRowIdx, c: ci });
            if (ws[ref]) {
                ws[ref].s = {
                    font: { bold: true },
                    fill: { fgColor: { rgb: 'EEF5EE' } },
                };
            }
        });

        // Bold first column labels
        for (let ri = 1; ri < aoa.length; ri++) {
            const ref = XLSX.utils.encode_cell({ r: ri, c: 0 });
            if (ws[ref] && aoa[ri][0]) {
                ws[ref].s = { ...ws[ref].s, font: { ...(ws[ref].s?.font || {}), bold: true } };
            }
        }

        // Set column widths
        ws['!cols'] = headers.map((h, i) => ({ wch: i === 0 ? 16 : Math.max(h.length + 2, 14) }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Kitchen');
        XLSX.writeFile(wb, `kitchen-${selectedDate}.xlsx`);
    };

    const exportKitchenCSV = (columnsToExport) => {
        const data = buildKitchenSpreadsheetData(columnsToExport);
        if (!data) return;
        const { headers, rows } = data;
        const aoa = [headers, ...rows];
        const csvContent = aoa.map(row =>
            row.map(cell => {
                const str = String(cell ?? '');
                return str.includes(',') || str.includes('"') || str.includes('\n')
                    ? `"${str.replace(/"/g, '""')}"`
                    : str;
            }).join(',')
        ).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kitchen-${selectedDate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportDriversXLSX = () => {
        if (currentRecords.length === 0) return;
        const SERVICE_ORDER = ['Collection', 'ROUTE 1', 'ROUTE 2', 'ROUTE 3', 'ROUTE 4', 'ROUTE 5', 'ROUTE 6'];
        const grouped = {};
        currentRecords.forEach(r => {
            const svc = (r['Service name'] || '').trim();
            const key = SERVICE_ORDER.find(s => svc.toLowerCase() === s.toLowerCase()) || 'Other';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(r);
        });
        const orderedKeys = [...SERVICE_ORDER.filter(k => grouped[k]), ...(grouped['Other'] ? ['Other'] : [])];

        // Identify "main items" — those with a date prefix like "24/02 TUESDAY-"
        const dateItemRegex = /^\d{1,2}\/\d{2}\s+\w+[-–—\s]*/;
        // Strip size keywords to normalize column names (UPSIZE and NORMAL share a column)
        const stripSize = (n) =>
            n
                // Remove dash + NORMAL/UPSIZE optionally followed by measurement
                .replace(/\s*[-–—]\s*(?:NORMAL|UPSIZE)(?:\s*\d+g)?\b/gi, '')

                // Remove dash + measurement only (e.g. - 460g)
                .replace(/\s*[-–—]\s*\d+g\b/gi, '')

                // Remove (NORMAL) or (UPSIZE)
                .replace(/\s*\((?:NORMAL|UPSIZE)\)\s*/gi, '')

                .trim();
        const allMainItemNames = new Set();
        currentRecords.forEach(r => {
            const name = (r['Lineitem name'] || '').trim();
            if (dateItemRegex.test(name)) {
                const baseName = stripSize(name.replace(dateItemRegex, '').trim());
                allMainItemNames.add(baseName);
            }
        });
        const mainItemColumns = [...allMainItemNames].sort();

        // Build abbreviation for main item column headers
        const abbreviate = (name) => {
            // e.g. "CHEESY MINCE PANCAKE" → "CMP"
            const initials = name.split(/\s+/).map(w => w[0]).join('');
            return initials;
        };

        // Build headers: base cols + dynamic main-item cols
        const preQtyHeaders = ['Customer name', 'Order number', 'Customer phone', 'Service address', 'Lineitem name', 'Customisation'];
        const postQtyHeaders = ['Total', 'Payment Status', 'Service name'];
        const headers = [...preQtyHeaders, ...mainItemColumns.map(abbreviate), ...postQtyHeaders];
        const qtyStartCol = preQtyHeaders.length; // index where quantity columns begin
        const baseHeaders = preQtyHeaders; // kept for backward compat references
        const aoa = [headers];
        const separatorRows = [];
        const totalsRows = [];

        orderedKeys.forEach((key, gi) => {
            // Add separator row between groups
            if (gi > 0) {
                separatorRows.push(aoa.length);
                aoa.push(Array(headers.length).fill(''));
            }

            // Track totals per main item for this group (normal, upsize, custom separately)
            const groupNormal = {};
            const groupUpsize = {};
            const groupCustom = {};
            mainItemColumns.forEach(col => { groupNormal[col] = 0; groupUpsize[col] = 0; groupCustom[col] = 0; });

            grouped[key].forEach(r => {
                const rawName = (r['Lineitem name'] || '').trim();
                const qty = parseInt(r['Lineitem quantity'], 10) || 1;
                const isMainItem = dateItemRegex.test(rawName);
                const option = r['Lineitem option'] || '';

                const optionStr = option || '';

                let customisation = '';

                const driverCustomMatches = [...optionStr.matchAll(/CUSTOMISE\s*:{2}\s*(.*)/gi)];
                if (driverCustomMatches.length > 0) {
                    customisation = driverCustomMatches.map(m => m[1].trim()).filter(Boolean).join(',');
                }

                const row = [
                    r['Customer name'],
                    r['Order number'],
                    r['Customer phone'],
                    r['Service address'],
                    rawName, // Lineitem name
                    '', // Customisation — filled below
                    ...Array(mainItemColumns.length).fill(''),
                    r['Total'],
                    r['Payment Status'],
                    r['Service name']
                ];

                if (isMainItem) {
                    const baseName = stripSize(rawName.replace(dateItemRegex, '').trim());
                    const colIdx = mainItemColumns.indexOf(baseName);
                    if (colIdx !== -1) {
                        row[qtyStartCol + colIdx] = qty;
                        const isUpsize = /\bUPSIZE\b/i.test(rawName);
                        const hasCustom = customisation && customisation.length > 0;
                        if (hasCustom) {
                            groupCustom[baseName] += qty;
                        } else if (isUpsize) {
                            groupUpsize[baseName] += qty;
                        } else {
                            groupNormal[baseName] += qty;
                        }
                    }
                    // Put customisation text if any
                    row[5] = customisation;
                } else {
                    // Non-main item handling
                    if (customisation) {
                        // Fringe case: non-main item WITH customisation
                        row[5] = `${qty}x ${customisation}`;
                    } else {
                        // Normal non-main item
                        row[5] = `${qty}`;
                    }
                }

                aoa.push(row);
            });

            // Add 3 totals rows for this group: Normal, Upsize, Customisation
            const normalRow = Array(headers.length).fill('');
            normalRow[0] = `${key} — Normal`;
            mainItemColumns.forEach((col, ci) => { normalRow[qtyStartCol + ci] = groupNormal[col] || ''; });
            totalsRows.push(aoa.length);
            aoa.push(normalRow);

            const upsizeRow = Array(headers.length).fill('');
            upsizeRow[0] = `${key} — Upsize`;
            mainItemColumns.forEach((col, ci) => { upsizeRow[qtyStartCol + ci] = groupUpsize[col] || ''; });
            totalsRows.push(aoa.length);
            aoa.push(upsizeRow);

            const customRow = Array(headers.length).fill('');
            customRow[0] = `${key} — Custom`;
            mainItemColumns.forEach((col, ci) => { customRow[qtyStartCol + ci] = groupCustom[col] || ''; });
            totalsRows.push(aoa.length);
            aoa.push(customRow);
        });

        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // Style separator rows (blue)
        separatorRows.forEach(ri => {
            for (let ci = 0; ci < headers.length; ci++) {
                const cellRef = XLSX.utils.encode_cell({ r: ri, c: ci });
                if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
                ws[cellRef].s = { fill: { patternType: 'solid', fgColor: { rgb: '4D94D8' } } };
            }
        });

        // Style totals rows (bold, light green background)
        totalsRows.forEach(ri => {
            for (let ci = 0; ci < headers.length; ci++) {
                const cellRef = XLSX.utils.encode_cell({ r: ri, c: ci });
                if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
                ws[cellRef].s = {
                    fill: { patternType: 'solid', fgColor: { rgb: 'EEF5EE' } },
                    font: { bold: true }
                };
            }
        });

        // Highlight Customer name & Total yellow for Unpaid rows
        const paymentColIdx = headers.indexOf('Payment Status');
        const customerColIdx = headers.indexOf('Customer name');
        const totalColIdx = headers.indexOf('Total');
        const lineitemColIdx = headers.indexOf('Lineitem name');
        for (let ri = 1; ri < aoa.length; ri++) {
            if (separatorRows.includes(ri) || totalsRows.includes(ri)) continue;
            const paymentVal = (aoa[ri][paymentColIdx] || '').toString().trim().toLowerCase();
            if (paymentVal === 'unpaid') {
                [customerColIdx, totalColIdx].forEach(ci => {
                    const cellRef = XLSX.utils.encode_cell({ r: ri, c: ci });
                    if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
                    ws[cellRef].s = { fill: { patternType: 'solid', fgColor: { rgb: 'FFFF00' } } };
                });
            }

            // Determine item name and customisation for color coding
            const itemName = (aoa[ri][lineitemColIdx] || '').toString().trim().toUpperCase();
            const customVal = (aoa[ri][5] || '').toString().trim(); // Customisation column
            const hasDate = /^\d{1,2}\/\d{2}\s/.test(itemName);

            // Determine color with priority: red > blue > pink > orange > green
            let itemColor = null;
            if (!hasDate && itemName) {
                itemColor = 'f6c6ac'; // light orange — no date prefix
            }
            if (itemName.includes('225G')) {
                itemColor = 'b3e5a1'; // light green
            }
            if (itemName.includes('UPSIZE')) {
                itemColor = 'f1ceee'; // light pink
            }
            if (itemName.includes('460G')) {
                itemColor = 'a6c9eb'; // light blue (overwrites pink)
            }
            if (hasDate && customVal && customVal.length > 0 && !totalsRows.includes(ri)) {
                itemColor = 'ff0000'; // red (overwrites all)
            }

            if (itemColor) {
                // Color the Lineitem name cell
                const nameRef = XLSX.utils.encode_cell({ r: ri, c: lineitemColIdx });
                if (!ws[nameRef]) ws[nameRef] = { t: 's', v: '' };
                ws[nameRef].s = { fill: { patternType: 'solid', fgColor: { rgb: itemColor } } };

                // Color the corresponding quantity cell in the main item columns
                if (hasDate) {
                    const baseName = stripSize(itemName.replace(/^\d{1,2}\/\d{2}\s+\w+[-–—\s]*/, '').trim());
                    const colIdx = mainItemColumns.findIndex(c => c.toUpperCase() === baseName);
                    if (colIdx !== -1) {
                        const qtyRef = XLSX.utils.encode_cell({ r: ri, c: qtyStartCol + colIdx });
                        if (!ws[qtyRef]) ws[qtyRef] = { t: 's', v: '' };
                        ws[qtyRef].s = { fill: { patternType: 'solid', fgColor: { rgb: itemColor } } };
                    }
                }
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Drivers');
        XLSX.writeFile(wb, `drivers-${selectedDate}.xlsx`);
    };

    const handleExport = () => {
        if (activeTab === 'kitchen') {
            // Open column picker modal with all columns selected
            const allNames = kitchenSummary.map(([name]) => name);
            setSelectedColumns(allNames);
            setExportFormat('xlsx');
            setShowColumnPicker(true);
        } else {
            exportDriversXLSX();
        }
    };

    const toggleColumn = (name) => {
        setSelectedColumns(prev =>
            prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
        );
    };

    const handleColumnPickerExport = () => {
        setShowColumnPicker(false);
        if (exportFormat === 'pdf') {
            exportKitchenPDF(selectedColumns);
        } else if (exportFormat === 'csv') {
            exportKitchenCSV(selectedColumns);
        } else {
            exportKitchenXLSX(selectedColumns);
        }
    };

    return (
        <div className="happea">
            <h1 className="happea__title">Happea</h1>
            <p className="happea__subtitle">Upload your CSV to generate kitchen and driver sheets</p>

            <div className="happea__container">
                {/* INPUT SIDE */}
                <div className="happea__panel happea__panel--input">
                    <h2 className="happea__panel-title">
                        <CloudUploadIcon style={{ fontSize: '1.4rem' }} /> Input
                    </h2>

                    <div
                        className={`happea__dropzone ${dragging ? 'happea__dropzone--active' : ''} ${file ? 'happea__dropzone--has-file' : ''}`}
                        onDragEnter={handleDragIn}
                        onDragLeave={handleDragOut}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => !file && inputRef.current?.click()}
                    >
                        <input ref={inputRef} type="file" accept=".csv" onChange={handleFileSelect} hidden />

                        {file ? (
                            <div className="happea__file-info">
                                <InsertDriveFileIcon className="happea__file-icon" />
                                <span className="happea__file-name">{file.name}</span>
                                <button className="happea__file-remove" onClick={(e) => { e.stopPropagation(); removeFile(); }}>
                                    <CloseIcon style={{ fontSize: '1rem' }} />
                                </button>
                            </div>
                        ) : (
                            <div className="happea__dropzone-content">
                                <CloudUploadIcon className="happea__upload-icon" />
                                <p className="happea__dropzone-text">Drag & drop your CSV here</p>
                                <span className="happea__dropzone-or">or</span>
                                <span className="happea__dropzone-browse">Browse files</span>
                            </div>
                        )}
                    </div>

                    {file && (
                        <button className="happea__process-btn" onClick={processFile} disabled={processing}>
                            {processing ? 'Processing…' : 'Process File'}
                        </button>
                    )}
                </div>

                {/* DIVIDER */}
                <div className="happea__divider" />

                {/* OUTPUT SIDE */}
                <div className="happea__panel happea__panel--output">
                    <div className="happea__output-header">
                        <h2 className="happea__panel-title">Output</h2>
                        {groupedData && (
                            <button className="happea__export-btn" onClick={handleExport}>
                                <FileDownloadIcon style={{ fontSize: '1rem' }} />
                                Export {activeTab === 'kitchen' ? 'Kitchen' : 'XLSX'}
                            </button>
                        )}
                    </div>

                    {!groupedData ? (
                        <div className="happea__output-cards">
                            <div className="happea__output-card happea__output-card--empty">
                                <div className="happea__output-card-header">
                                    <RestaurantIcon className="happea__output-card-icon" />
                                    <h3>Kitchen</h3>
                                </div>
                                <div className="happea__output-card-preview">
                                    <p>Upload and process a CSV to see results</p>
                                </div>
                            </div>
                            <div className="happea__output-card happea__output-card--empty">
                                <div className="happea__output-card-header">
                                    <LocalShippingIcon className="happea__output-card-icon" />
                                    <h3>Drivers</h3>
                                </div>
                                <div className="happea__output-card-preview">
                                    <p>Upload and process a CSV to see results</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="happea__results">
                            {/* Date selector */}
                            <div className="happea__date-selector">
                                <label className="happea__date-selector-label">Select Date:</label>
                                <select
                                    className="happea__date-select"
                                    value={selectedDate || ''}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                >
                                    {sortedDateKeys.map(key => (
                                        <option key={key} value={key}>
                                            {key === 'unknown' ? 'No Date Found' : formatDateLabel(key)}
                                        </option>
                                    ))}
                                </select>
                                <span className="happea__date-record-count">
                                    {currentRecords.length} record{currentRecords.length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            {/* Tabs */}
                            <div className="happea__tabs">
                                <button
                                    className={`happea__tab ${activeTab === 'kitchen' ? 'happea__tab--active' : ''}`}
                                    onClick={() => setActiveTab('kitchen')}
                                >
                                    <RestaurantIcon style={{ fontSize: '1rem' }} /> Kitchen
                                </button>
                                <button
                                    className={`happea__tab ${activeTab === 'drivers' ? 'happea__tab--active' : ''}`}
                                    onClick={() => setActiveTab('drivers')}
                                >
                                    <LocalShippingIcon style={{ fontSize: '1rem' }} /> Drivers
                                </button>
                            </div>

                            {/* Tab content */}
                            <div className="happea__tab-content">
                                {activeTab === 'kitchen' ? (
                                    <div className="happea__records-table-wrap">
                                        {kitchenSummary.length === 0 ? (
                                            <p className="happea__empty-cell">No records for this date</p>
                                        ) : (
                                            <table className="happea__records-table happea__kitchen-grid">
                                                <thead>
                                                    <tr>
                                                        <th className="happea__kitchen-row-label"></th>
                                                        {kitchenSummary.map(([name], idx) => (
                                                            <th key={idx}>{name}</th>
                                                        ))}
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {/* Normal row */}
                                                    <tr>
                                                        <td className="happea__kitchen-row-label">Normal</td>
                                                        {kitchenSummary.map(([, data], idx) => (
                                                            <td key={idx} className="happea__cell-qty">{data.normal || '—'}</td>
                                                        ))}
                                                        <td className="happea__cell-qty happea__cell-total">{kitchenSummary.reduce((s, [, d]) => s + d.normal, 0)}</td>
                                                    </tr>
                                                    {/* Normal Custom row */}
                                                    <tr className="happea__customise-row">
                                                        <td className="happea__kitchen-row-label">Normal Custom</td>
                                                        {kitchenSummary.map(([, data], idx) => (
                                                            <td key={idx} className="happea__cell-customise">
                                                                {data.normalCustomTexts.length > 0 ? (
                                                                    <ul className="happea__customise-list">
                                                                        {data.normalCustomTexts.map((c, ci) => (
                                                                            <li key={ci}><strong>{c.qty}x</strong> {c.text} <strong>({c.customer})</strong></li>
                                                                        ))}
                                                                    </ul>
                                                                ) : '—'}
                                                            </td>
                                                        ))}
                                                        <td className="happea__cell-qty happea__cell-total">{kitchenSummary.reduce((s, [, d]) => s + d.normalCustom, 0)}</td>
                                                    </tr>
                                                    {/* Upsize row */}
                                                    <tr>
                                                        <td className="happea__kitchen-row-label">Upsize</td>
                                                        {kitchenSummary.map(([, data], idx) => (
                                                            <td key={idx} className="happea__cell-qty">{data.upsize || '—'}</td>
                                                        ))}
                                                        <td className="happea__cell-qty happea__cell-total">{kitchenSummary.reduce((s, [, d]) => s + d.upsize, 0)}</td>
                                                    </tr>
                                                    {/* Upsize Custom row */}
                                                    <tr className="happea__customise-row">
                                                        <td className="happea__kitchen-row-label">Upsize Custom</td>
                                                        {kitchenSummary.map(([, data], idx) => (
                                                            <td key={idx} className="happea__cell-customise">
                                                                {data.upsizeCustomTexts.length > 0 ? (
                                                                    <ul className="happea__customise-list">
                                                                        {data.upsizeCustomTexts.map((c, ci) => (
                                                                            <li key={ci}><strong>{c.qty}x</strong> {c.text} <strong>({c.customer})</strong></li>
                                                                        ))}
                                                                    </ul>
                                                                ) : '—'}
                                                            </td>
                                                        ))}
                                                        <td className="happea__cell-qty happea__cell-total">{kitchenSummary.reduce((s, [, d]) => s + d.upsizeCustom, 0)}</td>
                                                    </tr>
                                                </tbody>
                                                <tfoot>
                                                    <tr className="happea__kitchen-total">
                                                        <td className="happea__kitchen-row-label">Total</td>
                                                        {kitchenSummary.map(([, data], idx) => (
                                                            <td key={idx} className="happea__cell-qty">{data.normal + data.normalCustom + data.upsize + data.upsizeCustom}</td>
                                                        ))}
                                                        <td className="happea__cell-qty happea__cell-total">{kitchenSummary.reduce((s, [, d]) => s + d.normal + d.normalCustom + d.upsize + d.upsizeCustom, 0)}</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        )}
                                    </div>
                                ) : (
                                    <div className="happea__records-table-wrap">
                                        <table className="happea__records-table">
                                            <thead>
                                                <tr>
                                                    <th>Customer</th>
                                                    <th>Order #</th>
                                                    <th>Phone</th>
                                                    <th>Address</th>
                                                    <th className="happea__col-item">Item</th>
                                                    <th>Qty</th>
                                                    <th>Total</th>
                                                    <th>Status</th>
                                                    <th>Note</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const SERVICE_ORDER = ['Collection', 'ROUTE 1', 'ROUTE 2', 'ROUTE 3', 'ROUTE 4', 'ROUTE 5', 'ROUTE 6'];
                                                    const grouped = {};
                                                    currentRecords.forEach(r => {
                                                        const svc = (r['Service name'] || '').trim();
                                                        const key = SERVICE_ORDER.find(s => svc.toLowerCase() === s.toLowerCase()) || 'Other';
                                                        if (!grouped[key]) grouped[key] = [];
                                                        grouped[key].push(r);
                                                    });
                                                    const orderedKeys = [...SERVICE_ORDER.filter(k => grouped[k]), ...(grouped['Other'] ? ['Other'] : [])];
                                                    if (orderedKeys.length === 0) {
                                                        return <tr><td colSpan={9} className="happea__empty-cell">No records for this date</td></tr>;
                                                    }
                                                    return orderedKeys.map(key => (
                                                        <>
                                                            <tr key={`header-${key}`} className={`happea__group-header happea__group--${key.toLowerCase().replace(/\s+/g, '-')}`}>
                                                                <td colSpan={9} className="happea__group-label">
                                                                    <LocalShippingIcon style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 6 }} />
                                                                    {key} ({grouped[key].length})
                                                                </td>
                                                            </tr>
                                                            {grouped[key].map((r, idx) => (
                                                                <tr key={`${key}-${idx}`} className={`happea__group-row happea__group--${key.toLowerCase().replace(/\s+/g, '-')}`}>
                                                                    <td>{r['Customer name']}</td>
                                                                    <td>{r['Order number']}</td>
                                                                    <td>{r['Customer phone']}</td>
                                                                    <td>{r['Service address']}{r['Service city'] ? ` - ${r['Service city']}` : ''}</td>
                                                                    <td className="happea__cell-item">{r['Lineitem name']}</td>
                                                                    <td>{r['Lineitem quantity']}</td>
                                                                    <td>{r['Total']}</td>
                                                                    <td>
                                                                        <span className={`happea__status ${r['Payment Status']?.toLowerCase() === 'paid' ? 'happea__status--paid' : ''}`}>
                                                                            {r['Payment Status']}
                                                                        </span>
                                                                    </td>
                                                                    <td>{r['Internal note']}</td>
                                                                </tr>
                                                            ))}
                                                        </>
                                                    ));
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Column Picker Modal */}
            {showColumnPicker && (
                <div className="happea__modal-overlay" onClick={() => setShowColumnPicker(false)}>
                    <div className="happea__modal" onClick={e => e.stopPropagation()}>
                        <div className="happea__modal-header">
                            <h3>Export Kitchen Sheet</h3>
                            <button className="happea__modal-close" onClick={() => setShowColumnPicker(false)}>
                                <CloseIcon style={{ fontSize: '1.2rem' }} />
                            </button>
                        </div>
                        <div className="happea__modal-body">
                            {/* Format selector */}
                            <div className="happea__format-selector">
                                <span className="happea__format-label">Format:</span>
                                <div className="happea__format-options">
                                    {[['xlsx', 'XLSX'], ['csv', 'CSV'], ['pdf', 'PDF']].map(([val, label]) => (
                                        <label key={val} className={`happea__format-option ${exportFormat === val ? 'happea__format-option--active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="exportFormat"
                                                value={val}
                                                checked={exportFormat === val}
                                                onChange={() => setExportFormat(val)}
                                                hidden
                                            />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="happea__modal-actions-row">
                                <button className="happea__modal-link" onClick={() => setSelectedColumns(kitchenSummary.map(([n]) => n))}>Select All</button>
                                <button className="happea__modal-link" onClick={() => setSelectedColumns([])}>Deselect All</button>
                            </div>
                            <ul className="happea__column-list">
                                {kitchenSummary.map(([name, data]) => {
                                    const total = data.normal + data.normalCustom + data.upsize + data.upsizeCustom;
                                    return (
                                        <li key={name} className="happea__column-item" onClick={() => toggleColumn(name)}>
                                            <input
                                                type="checkbox"
                                                checked={selectedColumns.includes(name)}
                                                onChange={() => toggleColumn(name)}
                                                className="happea__column-checkbox"
                                            />
                                            <span className="happea__column-name">{name}</span>
                                            <span className="happea__column-count">{total}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        <div className="happea__modal-footer">
                            <button className="happea__modal-btn cancel" onClick={() => setShowColumnPicker(false)}>Cancel</button>
                            <button className="happea__modal-btn confirm" onClick={handleColumnPickerExport} disabled={selectedColumns.length === 0}>
                                Export {exportFormat.toUpperCase()} ({selectedColumns.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
